import React, {useCallback, useMemo, useState} from 'react'
import type {Pull} from '../types/pull'

interface ArchiveDialogState {
  isOpen: boolean
  taskId?: string
  repoOwner?: string
  repoName?: string
  pull?: Pull
}

export const ARCHIVE_STATUS = {
  IDLE: 'idle',
  ERROR: 'error',
  ERROR_PR_CLOSE_FAILED: 'error_pr_close_failed',
  ERROR_PR_REOPEN_FAILED: 'error_pr_reopen_failed',
  UNDO_ERROR: 'undo_error',
} as const

export type ArchiveStatus = (typeof ARCHIVE_STATUS)[keyof typeof ARCHIVE_STATUS]

interface ArchiveDialogContextValue {
  dialogState: ArchiveDialogState
  archiveStatus: ArchiveStatus
  openArchiveDialog: (taskId: string, repoOwner?: string, repoName?: string, pull?: Pull) => void
  closeArchiveDialog: () => void
  setArchiveStatus: (status: ArchiveStatus) => void
}

const ArchiveDialogContext = React.createContext<ArchiveDialogContextValue | undefined>(undefined)

interface ArchiveDialogProviderProps {
  children: React.ReactNode
}

export function ArchiveDialogProvider({children}: ArchiveDialogProviderProps) {
  const [dialogState, setDialogState] = useState<ArchiveDialogState>({
    isOpen: false,
  })
  const [archiveStatus, setArchiveStatus] = useState<ArchiveStatus>(ARCHIVE_STATUS.IDLE)

  const openArchiveDialog = useCallback((taskId: string, repoOwner?: string, repoName?: string, pull?: Pull) => {
    setArchiveStatus(ARCHIVE_STATUS.IDLE)
    setDialogState({isOpen: true, taskId, repoOwner, repoName, pull})
  }, [])

  const closeArchiveDialog = useCallback(() => {
    setDialogState(prev => ({...prev, isOpen: false}))
  }, [])

  const value = useMemo(
    () => ({dialogState, archiveStatus, openArchiveDialog, closeArchiveDialog, setArchiveStatus}),
    [dialogState, archiveStatus, openArchiveDialog, closeArchiveDialog],
  )

  return <ArchiveDialogContext value={value}>{children}</ArchiveDialogContext>
}

export function useArchiveDialog() {
  const context = React.use(ArchiveDialogContext)
  if (!context) {
    throw new Error('useArchiveDialog must be used within an ArchiveDialogProvider')
  }
  return context
}

/**
 * Optional version of useArchiveDialog that returns undefined if not within a provider.
 * Useful for components that may be rendered in contexts where archiving isn't available.
 */
export function useArchiveDialogOptional() {
  return React.use(ArchiveDialogContext)
}
